<script>
import { onMounted, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGarageStore } from "@/stores/garage.js";
import { useModalStore } from '@/stores/modal.js'
import { indexCountries } from '@/lib/api/countries.js'
import { indexUsStates } from '@/lib/api/us_states.js'
import { createUserAddress, updateUserAddress } from '@/lib/api/user_addresses.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGeocodeStore } from '@/stores/geocode.js'
import { useToastStore } from '@/stores/toast.js'

export default {
  setup() {
    // I18n
    const { t } = useI18n();
    const translationPrefix = ref('modal.add_address');

    // Stores
    const garageStore = useGarageStore();
    const modalStore = useModalStore();
    const authenticationStore = useAuthenticationStore();
    const geocodeStore = useGeocodeStore();
    const toastStore = useToastStore();

    // Variables
    const isLoading = ref(true);
    const states = ref ([]);
    const countries = ref([]);
    const country = ref (null);
    const isDev = !import.meta.env.PROD;

    onMounted(async () => {
      if (isDev) {
        await getCountries()
      } else {
        await getCountry()
      }
      await getStates()
    });

    watch(() => modalStore.newUserAddress.us_state_id, (val) => {
      const state = states.value.find(x => x.id === val)
      if (state && state.abbreviation) {
        modalStore.newUserAddress.state_abr = state.abbreviation
      }
    })

    const getCountries = async () => {
      let params = new URLSearchParams();
      params.set('query[archived_eq]', 'false');

      await indexCountries(params)
        .then((response) => {
          countries.value = response.filter(c => c.abbreviation === 'US' || c.abbreviation === 'CA');
          country.value = countries.value.find(c => c.abbreviation === 'US');
          modalStore.newUserAddress.country_id = country.value?.id;
        })
        .catch((error) => {
          console.error('Error fetching countries:', error);
          getCountry();
        });
    }

    const getCountry = async () => {
      let params = new URLSearchParams();
      params.set('query[abbreviation_eq]', 'US');
      params.set('query[archived_eq]', 'false');

      await indexCountries(params)
        .then((response) => {
          country.value = response[0];
          modalStore.newUserAddress.country_id = country.value.id;
          modalStore.newUserAddress.country = 'USA';
        });
    }

    const getStates = async () => {
      if (!country.value) return;
      
      let params = new URLSearchParams();
      params.set('query[country_id_eq]', country.value.id);
      params.set('query[archived_eq]', 'false');
      params.set('query[s]', 'name ASC');

      await indexUsStates(params)
        .then((response) => {
          states.value = response;
        });
    }

    const onCountryChange = async () => {
      country.value = countries.value.find(c => c.id === modalStore.newUserAddress.country_id);
      modalStore.newUserAddress.country = country.value?.abbreviation === 'US' ? 'USA' : country.value?.name;
      modalStore.newUserAddress.us_state_id = t(`${translationPrefix.value}.state_placeholder`);
      modalStore.newUserAddress.state_abr = '';
      await getStates();
    }

    // Handle save action
    watch(() => modalStore.positiveHandleTrigger, async (value) => {
      if (!value) return
      
      if (modalStore.modalType === modalStore.modalTypeConstants.ADD_ADDRESS) {
        await handleCreateAddress()
      } else if (modalStore.modalType === modalStore.modalTypeConstants.EDIT_ADDRESS) {
        await handleUpdateAddress()
      }
    })

    const handleCreateAddress = async () => {
      if (!authenticationStore.currentUser?.user?.id) return
      
      if (!modalStore.newUserAddress.address || !modalStore.newUserAddress.city || !modalStore.newUserAddress.us_state_id || modalStore.newUserAddress.us_state_id === t('modal.add_address.state_placeholder')) {
        modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, {...modalStore.newUserAddress, error: {type: 'validation_error'}})
        return
      }
      
      modalStore.newUserAddress.user_id = authenticationStore.currentUser.user.id

      try {
        const location = (await geocodeStore.fetchAddressLocation(modalStore.newUserAddress))[0]
        modalStore.newUserAddress.latitude = location.lat
        modalStore.newUserAddress.longitude = location.lon

        await createUserAddress(modalStore.newUserAddress)
        modalStore.resetModal()
        toastStore.success(t('settings.saved_location_section.location_saved_success'))
        // Emit event to refresh saved locations
        window.dispatchEvent(new CustomEvent('addressSaved'))
      } catch (error) {
        modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, {...modalStore.newUserAddress, error: {type: 'geocoding_error'}})
      }
    }

    const handleUpdateAddress = async () => {
      if (!authenticationStore.currentUser?.user?.id) return
      
      if (!modalStore.newUserAddress.address || !modalStore.newUserAddress.city || !modalStore.newUserAddress.us_state_id || modalStore.newUserAddress.us_state_id === t('modal.add_address.state_placeholder')) {
        modalStore.setModal(modalStore.modalTypeConstants.EDIT_ADDRESS, {...modalStore.newUserAddress, error: {type: 'validation_error'}})
        return
      }
      
      modalStore.newUserAddress.user_id = authenticationStore.currentUser.user.id

      try {
        const location = (await geocodeStore.fetchAddressLocation(modalStore.newUserAddress))[0]
        modalStore.newUserAddress.latitude = location.lat
        modalStore.newUserAddress.longitude = location.lon

        await updateUserAddress(modalStore.newUserAddress.id, modalStore.newUserAddress)
        modalStore.resetModal()
        toastStore.success(t('settings.saved_location_section.location_saved_success'))
        // Emit event to refresh saved locations
        window.dispatchEvent(new CustomEvent('addressSaved'))
      } catch (error) {
        modalStore.setModal(modalStore.modalTypeConstants.EDIT_ADDRESS, {...modalStore.newUserAddress, error: {type: 'geocoding_error'}})
      }
    }

    return {
      t,
      translationPrefix,
      modalStore,
      garageStore,
      isLoading,
      states,
      countries,
      isDev,
      onCountryChange
    }
  },
};
</script>

<template>
  <div class="flex flex-col space-y-4 w-full px-4 md:px-8 py-4">
    <div class="container mt-2">
      <div class="flex flex-col px-0 sm:px-2 space-y-3">
        <div class="w-full">
          <label for="nickname" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.location_name_label`) }}</label>
          <input type="text" id="nickname" name="nickname" autocomplete="nickname" v-model="modalStore.newUserAddress.nickname" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t(`${translationPrefix}.location_name_placeholder`)">
        </div>
        <div class="w-full">
          <label for="address" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.street_address_label`) }}</label>
          <input type="text" id="address" name="address" autocomplete="street-address" v-model="modalStore.newUserAddress.address" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t(`${translationPrefix}.street_address_label`)">
        </div>
        <div class="w-full">
          <label for="address_2" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.street_address_2_label`) }}</label>
          <input type="text" id="address_2" name="address_2" autocomplete="address-line2" v-model="modalStore.newUserAddress.address_2" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t(`${translationPrefix}.street_address_2_label`)">
        </div>
        <div class="w-full">
          <label for="city" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.city_label`) }}</label>
          <input type="text" id="city" name="city" autocomplete="address-level2" v-model="modalStore.newUserAddress.city" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t(`${translationPrefix}.city_label`)">
        </div>

        <!-- Country Dropdown (Dev/QA only) -->
        <div v-if="isDev" class="w-full mb-2">
          <label for="country" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.country_label`) }}</label>
          <div class="relative">
            <select
              id="country"
              name="country"
              autocomplete="country"
              v-model="modalStore.newUserAddress.country_id"
              @change="onCountryChange"
              class="appearance-none w-full mt-1 p-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300"
            >
              <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <img src="../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
            </div>
          </div>
        </div>
        
        <!-- Hidden country field for production -->
        <input v-if="!isDev" type="hidden" v-model="modalStore.newUserAddress.country" value="USA" />

        <div class="flex flex-col md:flex-row justify-between items-center border-gray-300 space-x-0 md:space-x-2">
          <div class="w-full md:w-1/2 mb-2 md:mb-0">
            <label for="state" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.state_label`) }}</label>
            <div class="relative">
              <select
                id="state"
                name="state"
                autocomplete="address-level1"
                v-model="modalStore.newUserAddress.us_state_id"
                class="appearance-none w-full mt-1 p-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300"
              >
                <option :value="t(`${translationPrefix}.state_placeholder`)" disabled>{{ t(`${translationPrefix}.state_placeholder`) }}</option>
                <option v-for="state in states" :key="state.id" :value="state.id">{{ state.name }}</option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <img src="../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/2 mb-2 md:mb-0">
            <label for="zip" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.zip_label`) }}</label>
            <input type="text" id="zip" name="zip" autocomplete="postal-code" v-model="modalStore.newUserAddress.zip" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t(`${translationPrefix}.zip_label`)">
          </div>
        </div>

        <div class="py-2" v-if="modalStore.newUserAddress.error">
          <p class="text-red-600">{{ 
            modalStore.newUserAddress.error.type && t(`${translationPrefix}.${modalStore.newUserAddress.error.type}`) !== `${translationPrefix}.${modalStore.newUserAddress.error.type}` 
              ? t(`${translationPrefix}.${modalStore.newUserAddress.error.type}`) 
              : t(`${translationPrefix}.unknown_error`) 
          }}</p>
        </div>

      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/modals/garage';
</style>
